/**
 * Appearance Screen - MOBILE ONLY
 * 
 * Theme preferences with live preview.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useThemePreference, ThemePreference } from '../../../hooks/useThemePreference';
import { color, text, space, radius, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard } from '../../../components/v2/mobile';

export default function AppearanceScreen() {
  const router = useRouter();
  const { preference, effectiveTheme, isLoading, setPreference } = useThemePreference();

  const handleBack = useCallback(() => router.back(), [router]);
  const handleThemeSelect = useCallback((theme: ThemePreference) => setPreference(theme), [setPreference]);

  if (isLoading) {
    return (
      <ScreenContainer>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </ScreenContainer>
    );
  }

  const previewLabel = preference === 'light' ? 'Light Mode' 
    : preference === 'dark' ? 'Dark Mode' 
    : `System Default (${effectiveTheme === 'light' ? 'Light' : 'Dark'})`;

  return (
    <ScreenContainer>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Appearance</Text>
          <View style={styles.spacer} />
        </View>

        {/* THEME OPTIONS */}
        <View style={styles.section}>
          <SectionHeader label="Theme" />
          <ListCard>
            <ThemeOption icon="sunny-outline" title="Light" subtitle="Always use light mode" selected={preference === 'light'} onPress={() => handleThemeSelect('light')} />
            <ThemeOption icon="moon-outline" title="Dark" subtitle="Always use dark mode" selected={preference === 'dark'} onPress={() => handleThemeSelect('dark')} />
            <ThemeOption icon="phone-portrait-outline" title="System" subtitle="Match device settings" selected={preference === 'system'} onPress={() => handleThemeSelect('system')} hideBorder />
          </ListCard>
        </View>

        {/* PREVIEW */}
        <View style={styles.section}>
          <SectionHeader label="Preview" />
          <View style={styles.previewContainer}>
            <View style={[styles.previewCard, effectiveTheme === 'dark' && styles.previewCardDark]}>
              <View style={[styles.previewHeader, effectiveTheme === 'dark' && styles.previewHeaderDark]} />
              <View style={styles.previewContent}>
                <View style={[styles.previewLine, effectiveTheme === 'dark' && styles.previewLineDark]} />
                <View style={[styles.previewLine, styles.previewLineShort, effectiveTheme === 'dark' && styles.previewLineDark]} />
              </View>
            </View>
            <Text style={styles.previewLabel}>{previewLabel}</Text>
          </View>
        </View>

        {/* INFO */}
        <View style={styles.infoContainer}>
          <Text style={styles.infoText}>Note: Dark mode is a preview feature. Some screens may not fully support it yet.</Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// THEME OPTION COMPONENT
// =============================================================================

interface ThemeOptionProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  subtitle: string;
  selected: boolean;
  onPress: () => void;
  hideBorder?: boolean;
}

function ThemeOption({ icon: iconName, title, subtitle, selected, onPress, hideBorder }: ThemeOptionProps) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.optionRow, !hideBorder && styles.optionRowBorder, pressed && styles.pressed]}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
    >
      <Ionicons name={iconName} size={icon.md} color={color.textSecondary} />
      <View style={styles.optionContent}>
        <Text style={styles.optionTitle}>{title}</Text>
        <Text style={styles.optionSubtitle}>{subtitle}</Text>
      </View>
      {selected && <Ionicons name="checkmark" size={icon.lg} color={color.primary} />}
    </Pressable>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: layout.scrollBottomPadding },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.sm, paddingVertical: space.sm },
  backButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, textAlign: 'center' },
  spacer: { width: touch.minimum },
  section: { paddingHorizontal: space.md, marginBottom: space.lg },
  optionRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: space.md, paddingHorizontal: space.md, gap: space.sm + space.xs },
  optionRowBorder: { borderBottomWidth: border.width.thin, borderBottomColor: color.divider },
  pressed: { backgroundColor: color.pressed },
  optionContent: { flex: 1 },
  optionTitle: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary },
  optionSubtitle: { fontSize: text.caption.fontSize, color: color.textSecondary, marginTop: space.xs / 2 },
  previewContainer: { alignItems: 'center', paddingVertical: space.lg },
  previewCard: { width: 120, height: 200, backgroundColor: color.surface, borderRadius: radius.md, borderWidth: border.width.thin, borderColor: color.border, overflow: 'hidden' },
  previewCardDark: { backgroundColor: '#1C1C1E', borderColor: '#38383A' },
  previewHeader: { height: 24, backgroundColor: color.primary },
  previewHeaderDark: { backgroundColor: '#2D5A3D' },
  previewContent: { flex: 1, padding: space.sm, gap: space.sm },
  previewLine: { height: 8, backgroundColor: color.backgroundSecondary, borderRadius: radius.sm },
  previewLineDark: { backgroundColor: '#2C2C2E' },
  previewLineShort: { width: '60%' },
  previewLabel: { marginTop: space.md, fontSize: text.caption.fontSize, color: color.textSecondary },
  infoContainer: { paddingHorizontal: space.lg, paddingVertical: space.md },
  infoText: { fontSize: text.caption.fontSize, color: color.textMuted, textAlign: 'center', lineHeight: text.body.lineHeight },
});

